import {
  Timeline,
  TimelineItem,
  TimelineOppositeContent,
  TimelineConnector,
  TimelineDot,
  TimelineSeparator,
  TimelineContent,
} from '@mui/lab';
import React, { useEffect } from 'react';
import {
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Stack,
} from '@mui/material';
import LoginState from './LoginState';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CommentIcon from '@mui/icons-material/Comment';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { green, blue, red } from '@mui/material/colors';

const FileItem = ({ orderID, file, download, remove }) => (
  <TimelineItem>
    <TimelineOppositeContent
      // style={{ paddingLeft: '0px', paddingRight: '0px' }}
      sx={{ m: 'auto 0', flex: 0 }}
      align="right"
      variant="body2"
      color="text.secondary"
    >
      {file.timestamp.split(' ')[0]}
    </TimelineOppositeContent>
    <TimelineSeparator>
      <TimelineConnector />
      <TimelineDot>
        <InsertDriveFileIcon />
      </TimelineDot>
      <TimelineConnector />
    </TimelineSeparator>
    <TimelineContent>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'grey.100',
            p: 1,
            pl: 2,
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" style={{color: file.approved === 'True' ? '#1c6910' : '#50544f'}}>{file.name}</Typography>
          <IconButton sx={{ m: 0 }} onClick={() => download(orderID, file.name)}>
            <DownloadIcon />
          </IconButton>
        </Box>
        {LoginState.userRole === 'Admin' && (
          <IconButton sx={{ ml: 1 }} onClick={() => remove(orderID, file.name)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
      <Typography variant="caption">{file.user}</Typography>
    </TimelineContent>
  </TimelineItem>
);

const CommentItem = ({ orderID, comment, remove }) => (
  <TimelineItem>
    <TimelineOppositeContent
      // style={{ paddingLeft: '0px', paddingRight: '0px' }}
      sx={{ m: 'auto 0', flex: 0 }}
      align="right"
      variant="body2"
      color="text.secondary"
    >
      {comment.timestamp.split(' ')[0]}
    </TimelineOppositeContent>
    <TimelineSeparator>
      <TimelineConnector />
      <TimelineDot>
        <CommentIcon />
      </TimelineDot>
      <TimelineConnector />
    </TimelineSeparator>
    <TimelineContent>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'grey.100',
            p: 1,
            pl: 2,
            borderRadius: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1">{comment.commentText || '-'}</Typography>
          <Typography color="grey.700" variant="caption">
            {comment.userName}
          </Typography>
        </Box>
        {LoginState.userRole === 'Admin' && (
          <IconButton sx={{ ml: 1 }} onClick={() => remove(orderID, 'comment-' + comment.timestamp)}>
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </TimelineContent>
  </TimelineItem>
);
const ApprovalItem = ({ approval }) => {
  const lenderApproval = approval.commentText.includes('Lender]');
  return (
    <TimelineItem>
      <TimelineOppositeContent
        // style={{ paddingLeft: '0px', paddingRight: '0px' }}
        sx={{ m: 'auto 0', flex: 0 }}
        align="right"
        variant="body2"
        color="text.secondary"
      >
        {approval.timestamp.split(' ')[0]}
      </TimelineOppositeContent>
      <TimelineSeparator>
        <TimelineConnector />
        <TimelineDot
          sx={{ backgroundColor: lenderApproval ? green['600'] : blue['400'] }}
        >
          {lenderApproval ? <VerifiedIcon /> : <CheckIcon />}
        </TimelineDot>
        <TimelineConnector />
      </TimelineSeparator>
      <TimelineContent>
        <Box
          sx={{
            backgroundColor: lenderApproval ? green[50] : blue[50],
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="body1">{approval.commentText || '-'}</Typography>
          <Typography
            color={lenderApproval ? 'grey.900' : 'grey.700'}
            variant="caption"
          >
            {approval.userName}
          </Typography>
        </Box>
      </TimelineContent>
    </TimelineItem>
  );
};

// This Rejection item is to show change request items
const RejectionItem = ({ rejection }) => (
  <TimelineItem>
    <TimelineOppositeContent
      // style={{ paddingLeft: '0px', paddingRight: '0px' }}
      sx={{ m: 'auto 0', flex: 0 }}
      align="right"
      variant="body2"
      color="text.secondary"
    >
      {rejection.timestamp.split(' ')[0]}
    </TimelineOppositeContent>
    <TimelineSeparator>
      <TimelineConnector />
      <TimelineDot sx={{ backgroundColor: red['600'] }}>
        <CloseIcon />
      </TimelineDot>
      <TimelineConnector />
    </TimelineSeparator>
    <TimelineContent>
      <Box
        sx={{
          backgroundColor: red[50],
          p: 2,
          borderRadius: 2,
        }}
      >
        <Typography variant="body1">{rejection.commentText || '-'}</Typography>
        <Typography color="grey.700" variant="caption">
          {rejection.userName}
        </Typography>
      </Box>
    </TimelineContent>
  </TimelineItem>
);

export default function ReportList({ orderID, items, download, remove }) {

  const [display, setDisplay] = React.useState(items);
  useEffect(() => setDisplay(items), [items]);

  if (!display) {
    return (
      <Stack justifyContent="center" alignItems="center" my={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Timeline position="right" sx={{ px: 0 }}>
      {/* TODO: we need to add a type to items such as reject, comment, approval, that way we can clearly differentiate the items in the timeline */}
      {display.map((item) => {
        if (item.name) {
          return (
            <FileItem orderID={orderID} key={item.timestamp + item.name} file={item} download={download} remove={remove} />
          );
        } else {
          if (item.commentText.includes('[Approved') || item.commentText.includes('Review]')) {
            return <ApprovalItem key={item.timestamp + "ApprovalItem"} approval={item} />;
          } else {
            return <CommentItem orderID={orderID} key={item.timestamp + "Comment"} comment={item} remove={remove} />
          }
        }
      })}
    </Timeline>
  );
}
